import { baseApi } from '@alltrails/redux-helpers';
import type {
  AreaHit,
  AreaSearchResult,
  AreaType,
  PhotospotHit,
  PlaceSearchResult,
  POIHit,
  POISearchResult,
  TrailHit,
  TrailSearchResult
} from '@alltrails/types/AlgoliaResults';
import ExploreServicePayload, { ExploreServiceResultType } from '../types/ExploreServicePayload';
import ExploreServiceResponse, { type ExploreServiceSearchResult } from '../types/ExploreServiceResponse';
import type { POIDetailsResponse } from '../types/POIDetailsResponse';
import cleanExploreServicePayloadLocation from '../utils/cleanExploreServicePayloadLocation';

const url = '/api/alltrails/explore/v1/search';

const poiAttributesToRetrieve: (keyof POIHit)[] = [
  'ID',
  'location_label',
  'location_type',
  'name',
  'photos_count',
  'slug',
  'subtype',
  'popularity',
  'state_name',
  'trails_count',
  'type_label',
  '_geoloc'
];

const photospotAttributesToRetrieve: (keyof PhotospotHit)[] = [
  'ID',
  'objectID',
  'location_label',
  'location_type',
  'name',
  'popularity',
  'slug',
  'type_label',
  'subtype',
  '_geoloc',
  'photos_count',
  'trails_count',
  'state_name'
];

const trailAttributesToRetrieve: (keyof TrailHit)[] = [
  'ID',
  'area_name',
  'avg_rating',
  'area_slug',
  'city_url',
  'difficulty_rating',
  'description',
  'duration_minutes',
  'duration_minutes_cycling',
  'duration_minutes_hiking',
  'duration_minutes_mountain_biking',
  'duration_minutes_trail_running',
  'estimated_time_to_complete',
  'has_profile_photo',
  'is_closed',
  'length',
  'location_label',
  'name',
  'num_reviews',
  'num_photos',
  'popularity',
  'profile_photo_url',
  'slug',
  '_geoloc',
  '_cluster_geoloc'
];

const areaAttributesToRetrieve: (keyof AreaHit)[] = ['type', 'area_type'];

export const exploreServiceApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['List-Trails', 'Places', 'POI-Trails', 'Park-Trails', 'Trails'] })
  .injectEndpoints({
    endpoints: builder => ({
      getAreaTrails: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults'>,
        Pick<ExploreServicePayload, 'location'>
      >({
        query: ({ location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['boundingBox', 'searchResults']
          };
          return { url, data, method: 'post' };
        }
      }),
      getListTrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'> & { additionalAttributesToRetrieve?: (keyof TrailHit)[] }
      >({
        query: ({ additionalAttributesToRetrieve, location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: [...trailAttributesToRetrieve, ...(additionalAttributesToRetrieve || [])],
            recordTypesToReturn: ['trail'],
            resultsToInclude: ['searchResults', 'summary']
          };
          return {
            url,
            data,
            method: 'post'
          };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['List-Trails']
      }),
      getLocationData: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults'>,
        Pick<ExploreServicePayload, 'filters' | 'limit' | 'location' | 'page' | 'recordAttributesToRetrieve' | 'sort'>
      >({
        query: ({ location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['boundingBox', 'searchResults']
          };
          return { url, data, method: 'post' };
        }
      }),
      getParks: builder.mutation<
        Pick<ExploreServiceResponse<AreaSearchResult>, 'searchResults'>,
        Pick<ExploreServicePayload, 'limit' | 'location'> & { areaTypes?: AreaType[] }
      >({
        query: ({ areaTypes, limit, location }) => {
          const data: ExploreServicePayload = {
            filters: {
              collections: ['parks'],
              ...(areaTypes && { areaType: areaTypes })
            },
            ...(limit && { limit }),
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['searchResults']
          };
          return { url, data, method: 'post' };
        }
      }),
      getPlaces: builder.query<Pick<ExploreServiceResponse<PlaceSearchResult>, 'searchResults'>, Pick<ExploreServicePayload, 'location' | 'limit'>>({
        query: ({ location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordTypesToReturn: ['area', 'city', 'country', 'state'],
            resultsToInclude: ['searchResults'],
            sort: 'most_popular'
          };
          return { url, data, method: 'post' };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Places']
      }),
      getPOITrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult | POISearchResult>, 'place' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'>
      >({
        query: ({ location, ...payload }) => {
          const attributes: string[] = [...poiAttributesToRetrieve, ...trailAttributesToRetrieve, 'polyline'];
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            recordTypesToReturn: ['poi', 'trail'],
            resultsToInclude: ['place', 'searchResults', 'summary']
          };
          return { url, data, method: 'post' };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['POI-Trails']
      }),
      getParkTrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult | POISearchResult>, 'place' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'>
      >({
        query: ({ location, ...payload }) => {
          const attributes: string[] = [...poiAttributesToRetrieve, ...trailAttributesToRetrieve, 'polyline'];
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            recordTypesToReturn: ['poi', 'trail'],
            resultsToInclude: ['place', 'searchResults', 'summary']
          };
          return { url, data, method: 'post' };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Park-Trails']
      }),
      getResults: builder.query<
        Pick<
          ExploreServiceResponse<ExploreServiceSearchResult>,
          'boundingBox' | 'hitSummaryText' | 'queryId' | 'searchConfig' | 'searchResults' | 'summary'
        >,
        Pick<ExploreServicePayload, 'context' | 'filters' | 'location' | 'query' | 'recordTypeLimits' | 'recordTypesToReturn' | 'sort'> & {
          // When true, the server will activate the LLM logic, so this should only be true when a new query is entered, to reduce request time and cost
          triggerNaturalLanguageSearch?: boolean;
        }
      >({
        query: ({ location, triggerNaturalLanguageSearch = false, ...payload }) => {
          const attributes: string[] = [];
          if (payload.recordTypesToReturn?.includes('poi')) {
            attributes.push(...poiAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('trail')) {
            attributes.push(...trailAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('photospot')) {
            attributes.push(...photospotAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('area')) {
            attributes.push(...areaAttributesToRetrieve);
          }
          const resultsToInclude: ExploreServiceResultType[] = ['searchResults', 'summary'];
          if (triggerNaturalLanguageSearch) {
            resultsToInclude.push('boundingBox', 'hitSummaryText', 'searchConfig');
          }
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            resultsToInclude
          };
          return {
            url,
            data,
            method: 'post',
            config: {
              params: {
                nls: String(triggerNaturalLanguageSearch)
              }
            }
          };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Trails']
      }),
      getResultsBoundingBox: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'expandSearch' | 'filters' | 'location' | 'sort'>
      >({
        query: ({ location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['searchResults', 'boundingBox', 'summary']
          };
          return { url, data, method: 'post' };
        },
        invalidatesTags: ['Trails'],
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          const { ...rest } = args;
          const { data } = await queryFulfilled;
          rest.location = { ...data.boundingBox, mapRotation: 0 };
          dispatch(exploreServiceApi.util.upsertQueryData('getResults', rest, data));
        }
      }),
      getPOIDetails: builder.query<POIDetailsResponse, string>({
        query: slug => ({
          url: `/api/alltrails/points_of_interest/${slug}`,
          method: 'get'
        })
      })
    })
  });

export const {
  useGetAreaTrailsMutation,
  useGetListTrailsQuery,
  useGetLocationDataMutation,
  useGetParksMutation,
  useGetPOITrailsQuery,
  useGetParkTrailsQuery,
  useGetResultsQuery,
  useGetResultsBoundingBoxMutation,
  useLazyGetListTrailsQuery,
  useLazyGetPlacesQuery,
  useLazyGetPOIDetailsQuery
} = exploreServiceApi;
